import { Trash01, UploadCloud02 } from "@untitledui/icons";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { cx } from "@/utils/cx";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/svg+xml", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DIMENSIONS = { width: 800, height: 400 };

type LogoPickerProps = {
  currentLogoUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
};

const validateImageDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (file.type === "image/svg+xml") {
      resolve(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img.width <= MAX_DIMENSIONS.width && img.height <= MAX_DIMENSIONS.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
};

export const LogoPicker = ({ currentLogoUrl, onUpload, onDelete }: LogoPickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error("Invalid file type. Please upload SVG, PNG, JPG, GIF or WebP.");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error("File too large. Maximum size is 5MB.");
        return;
      }

      const validDimensions = await validateImageDimensions(file);
      if (!validDimensions) {
        toast.error(`Image dimensions exceed ${MAX_DIMENSIONS.width}×${MAX_DIMENSIONS.height}px.`);
        return;
      }

      await onUpload(file);
    },
    [onUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    },
    [handleFile],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleInputChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cx(
          "relative w-full flex flex-col items-center justify-center gap-2 px-6 py-4 rounded-lg border border-dashed transition-colors",
          isDragging ? "border-brand-600 bg-brand-50/10" : "border-secondary bg-primary",
          "hover:border-brand-600 hover:bg-brand-50/5 cursor-pointer",
        )}
      >
        {currentLogoUrl ? (
          <div className="relative group">
            <img src={currentLogoUrl} alt="Logo preview" className="max-h-16 max-w-full rounded object-contain" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className={cx(
                "absolute -top-2 -right-2 size-6 rounded-full bg-error flex items-center justify-center",
                "opacity-0 group-hover:opacity-100 transition-opacity",
                "hover:bg-error/90",
              )}
            >
              <Trash01 className="size-3 text-white" />
            </button>
          </div>
        ) : (
          <>
            <div className="size-10 rounded-full bg-secondary flex items-center justify-center">
              <UploadCloud02 className="size-5 text-tertiary" />
            </div>
            <div className="text-center">
              <span className="text-sm font-medium text-brand-600">Click to upload</span>
              <span className="text-sm text-tertiary"> or drag and drop</span>
            </div>
            <p className="text-xs text-tertiary">SVG, PNG, JPG or GIF (max. 800x400px)</p>
          </>
        )}
      </button>
    </div>
  );
};
